import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Container,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { ServiceEditorForm } from '../../../components/profileEditors/ServiceEditorForm';
import { ProvidersService } from '../../../services/ProvidersService';
import { MasterDataService } from '../../../services/MasterDataService';
import { getStudioRentalPrompt } from '../studioRentalPrompt';

const ServicesStep = ({ provider, setProvider, onNext, onBack }) => {
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(undefined);
  const [addNew, setAddNew] = useState(false);
  const [serviceError, setServiceError] = useState("");
  const [categoryConfigs, setCategoryConfigs] = useState([]);
  const [studioRentalChecked, setStudioRentalChecked] = useState(provider?.studioRentalInterest === true);

  // Category configs drive the optional studio-rental question; if they fail to load the question is simply not asked.
  useEffect(() => {
    let active = true;
    const loadCategoryConfigs = async () => {
      try {
        const configs = await MasterDataService.getCategoryConfigs();
        if (active) {
          setCategoryConfigs(Array.isArray(configs) ? configs : []);
        }
      } catch (err) {
        console.error('Failed to load category configs', err);
      }
    };
    loadCategoryConfigs();
    return () => {
      active = false;
    };
  }, []);

  const studioRentalPrompt = getStudioRentalPrompt(provider, categoryConfigs);

  // Adds the studio-rental answer to a provider being saved, only while the question is shown.
  const withStudioRentalInterest = (providerToSave) => {
    if (!studioRentalPrompt) {
      return providerToSave;
    }
    return {
      ...providerToSave,
      studioRentalInterest: studioRentalChecked,
      studioRentalInterestMessage: studioRentalPrompt.message,
      studioRentalInterestCategory: studioRentalPrompt.category
    };
  };

  const newService = () => ({
    category: "",
    name: "",
    offeredLengths: [{ length: 30, price: 59.99 }],
    description: ""
  });

  const removePendingNewService = (providerToUpdate) => {
    if (!addNew || !providerToUpdate?.servicesProvided?.length) {
      return providerToUpdate;
    }
    return {
      ...providerToUpdate,
      servicesProvided: providerToUpdate.servicesProvided.slice(
        0,
        providerToUpdate.servicesProvided.length - 1
      )
    };
  };

  const handleSelectService = (index) => {
    setServiceError("");
    // If we were in "Add New" mode, remove the pending service first
    if (addNew) {
      const cleaned = removePendingNewService(provider);
      setProvider(cleaned);
    }
    setAddNew(false);
    setSelectedServiceIndex(index);
  };

  const handleAddNewService = () => {
    setServiceError("");
    let updated = { ...provider };

    // If already in "Add New" mode, remove the pending service first
    if (addNew && updated.servicesProvided?.length) {
      updated.servicesProvided = updated.servicesProvided.slice(
        0,
        updated.servicesProvided.length - 1
      );
    }

    // Add the new blank service
    updated.servicesProvided = [...(updated.servicesProvided || []), newService()];
    setProvider(updated);
    setAddNew(true);
    setSelectedServiceIndex(updated.servicesProvided.length - 1);
  };

  const handleAddService = () => {
    const current = provider?.servicesProvided?.[selectedServiceIndex];
    if (!current?.category?.trim() || !current?.name?.trim()) {
      setServiceError("Please choose a category and a service before adding.");
      return;
    }
    setServiceError("");

    // If provider has no category or category is "NONE", set it from this first service
    if (!provider?.categoryType || provider.categoryType === "NONE") {
      const updated = {
        ...provider,
        categoryType: current.category
      };
      setProvider(updated);
    }

    setAddNew(false);
  };

  const handleRemoveService = async (index) => {
    const updated = withStudioRentalInterest({
      ...provider,
      servicesProvided: provider.servicesProvided.filter((_, i) => i !== index)
    });
    setProvider(updated);
    await ProvidersService.updateProvider(updated);

    if (updated.servicesProvided.length === 0) {
      setSelectedServiceIndex(undefined);
      setAddNew(false);
    } else {
      const nextIndex = Math.max(0, index - 1);
      setSelectedServiceIndex(nextIndex);
      setAddNew(false);
    }
  };

  const handleSaveAndContinue = () => {
    // Sanitize: remove services with empty category or name
    const cleaned = withStudioRentalInterest({
      ...provider,
      servicesProvided: (provider.servicesProvided || []).filter(
        (s) => s?.category?.trim() && s?.name?.trim()
      )
    });

    // Guard: must have at least one valid service
    if (cleaned.servicesProvided.length === 0) {
      setServiceError("Add at least one service to continue.");
      return;
    }

    onNext(cleaned);
  };

  const services = provider?.servicesProvided || [];
  const isValidServiceSelected =
    selectedServiceIndex !== undefined &&
    selectedServiceIndex >= 0 &&
    selectedServiceIndex < services.length;

  // Count services that have both category and name
  const validServices = services.filter(
    (s) => s?.category?.trim() && s?.name?.trim()
  );
  const hasValidServices = validServices.length > 0;

  return (
    <Container maxWidth="md" sx={{ paddingY: '20px' }}>
      <Typography variant="h4" sx={{ marginBottom: '20px' }}>
        Services Provided
      </Typography>
      <Box sx={{ marginBottom: '20px' }}>
        <Box
          component="ol"
          sx={{
            color: 'text.secondary',
            paddingLeft: '20px',
            margin: 0,
            fontSize: '0.875rem',
            lineHeight: 1.43
          }}
        >
          <Box component="li" sx={{ marginBottom: '8px' }}>
            Click "Add New Service"
          </Box>
          <Box component="li" sx={{ marginBottom: '8px' }}>
            Select a category
          </Box>
          <Box component="li" sx={{ marginBottom: '8px' }}>
            Select the service
          </Box>
          <Box component="li">
            Click "Add Service"
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '250px 1fr' }, gap: '20px' }}>
        {/* Service List */}
        <Box sx={{ borderRight: { xs: 'none', md: '1px solid #e0e0e0' }, paddingRight: { xs: 0, md: '20px' } }}>
          <List sx={{ width: '100%' }}>
            {services.map((service, index) => (
              <ListItemButton
                key={service.name || `service-${index}`}
                selected={selectedServiceIndex === index && !addNew}
                onClick={() => handleSelectService(index)}
              >
                <ListItemText primary={service.name || `Service ${index + 1}`} />
              </ListItemButton>
            ))}

            {services.length > 0 && <Divider sx={{ margin: '10px 0' }} />}

            <Button
              variant="contained"
              onClick={handleAddNewService}
              fullWidth
              sx={{ marginTop: '10px' }}
            >
              + Add New Service
            </Button>
          </List>
        </Box>

        {/* Service Editor Form */}
        <Box>
          {isValidServiceSelected ? (
            <Box>
              <ServiceEditorForm
                provider={provider}
                setProvider={setProvider}
                selectedServiceIndex={selectedServiceIndex}
                addNew={addNew}
              />

              {/* Add Service button and error message when in add new mode */}
              {addNew && (
                <Box sx={{ marginTop: '20px' }}>
                  {serviceError && (
                    <Alert severity="warning" sx={{ marginBottom: '10px' }}>
                      {serviceError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleAddService}
                    fullWidth
                  >
                    Add Service
                  </Button>
                </Box>
              )}

              {/* Remove button for existing service */}
              {!addNew && (
                <Box sx={{ marginTop: '20px' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveService(selectedServiceIndex)}
                    fullWidth
                  >
                    Remove Service
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', paddingY: '40px' }}>
              <Typography variant="body1" color="text.secondary">
                Add a service to get started.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {studioRentalPrompt && (
        <FormControlLabel
          sx={{ marginTop: '20px' }}
          control={
            <Checkbox
              checked={studioRentalChecked}
              onChange={(e) => setStudioRentalChecked(e.target.checked)}
              inputProps={{ 'data-testid': 'checkbox-studio-rental' }}
            />
          }
          label={studioRentalPrompt.message}
        />
      )}

      {/* Bottom Navigation */}
      <Divider sx={{ margin: '40px 0' }} />
      {!hasValidServices && (
        <Alert severity="info" sx={{ marginBottom: '20px' }}>
          Add at least one service to continue.
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" onClick={handleSaveAndContinue} disabled={!hasValidServices}>
          Save & Continue
        </Button>
      </Box>
    </Container>
  );
};

ServicesStep.propTypes = {
  provider: PropTypes.object.isRequired,
  setProvider: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default ServicesStep;
